import classNames from "classnames";
import isFinite from "lodash/isFinite";
import { ReadOnlyField } from "PFComponents/read_only_field";
import { TypographyProps } from "PFComponents/typography";
import { roundToDecimals } from "PFCore/helpers/rounding";
import { useGetColor } from "PFCore/hooks/use_get_color";
import { convertToPercentage } from "PFCore/utilities/math";
import { Currency, EconomicsScenario } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./budget_vs_cost_chart.module.scss";
import { BarChart } from "./parts/bar_chart";

type Unit = "m" | "k";
type BudgetVsCostChartProps = {
  className?: string;
  budget: number;
  cost: EconomicsScenario["cost"];
  headerVariant?: TypographyProps["variant"];
};

const getNumberInUnits = (number: number, unit?: Unit): { value: number; unit?: Unit } => {
  if (unit === "m" || number > 999999) {
    return { value: number / 1000000, unit: "m" };
  }
  if (unit === "k" || number > 999) {
    return { value: number / 1000, unit: "k" };
  }
  return { value: number };
};

export const BudgetVsCostChart = ({
  className,
  budget = 0,
  cost = { value: 0 },
  headerVariant
}: BudgetVsCostChartProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "scenario.charts.budgetVsCost" });
  const { unit, value: budgetValue } = getNumberInUnits(budget);
  const { value: costValue } = getNumberInUnits(cost.value, unit);
  const getColor = useGetColor();

  const costColor = useMemo(() => {
    if (costValue > budgetValue) {
      return getColor("paletteRed0");
    }
    if (costValue / budgetValue >= 0.9) {
      return getColor("paletteOrange0");
    }
    return getColor("paletteEcharts1");
  }, [budgetValue, costValue, getColor]);

  const profit = roundToDecimals(budgetValue - costValue, 2);
  const margin = Math.round((profit / budgetValue) * 100);
  const symbol = (cost.currency && Currency[cost.currency]) || "";
  const isMarginFinite = isFinite(margin);

  return (
    <div className={className}>
      <BarChart
        title={t("title")}
        headerVariant={headerVariant}
        hideCoverage
        symbol={symbol}
        unit={unit}
        series={[
          {
            name: t("budget"),
            data: [{ value: budgetValue, unit }]
          },
          {
            name: t("cost"),
            color: costColor,
            data: [{ value: costValue, unit }]
          }
        ]}
      />
      <div className={css.indicators}>
        <ReadOnlyField
          label={t("profit")}
          value={`${profit >= 0 ? "" : "-"}${symbol}${Math.abs(profit)}${unit || ""}`}
          classes={{ root: classNames({ [css.negative]: profit < 0 }) }}
        />
        <ReadOnlyField
          label={t("margin")}
          value={convertToPercentage(isMarginFinite ? margin : undefined, { isNormalized: true })}
          classes={{ root: classNames({ [css.negative]: profit < 0 && isMarginFinite }) }}
        />
      </div>
    </div>
  );
};
